import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, del, patch, uploadFile } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { SearchInput } from "@/components/common/SearchInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { relative } from "@/lib/format";
import { FileText, Plus, Trash2, ChevronDown, ChevronRight, Signature, AlertTriangle } from "lucide-react";
import { UploadProgress } from "@/components/common/UploadProgress";
import { toast } from "sonner";

const FIELD_TYPES = ["text", "number", "date", "textarea"];

function TemplateRow({ t, canEdit, canDelete, onDelete, employees }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [fields, setFields] = useState(t.fields);
  const emap = Object.fromEntries(employees.map((e) => [e.id, e]));
  const signatory = t.signatory_employee_id ? emap[t.signatory_employee_id] : null;

  const saveFields = useMutation({
    mutationFn: () => patch(`/doc-templates/${t.id}`, { fields }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["doc-templates", "hrms"] }); toast.success("Fields updated"); },
  });
  const setSignatory = useMutation({
    mutationFn: (signatory_employee_id) => patch(`/doc-templates/${t.id}`, { signatory_employee_id: signatory_employee_id || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["doc-templates", "hrms"] }); toast.success("Signatory updated"); },
  });

  const setField = (i, patch) => setFields((fs) => fs.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));

  return (
    <div data-testid={`tmpl-row-${t.id}`}>
      <div onClick={() => setOpen((s) => !s)} className="group flex cursor-pointer items-center justify-between px-4 py-3 text-sm hover:bg-muted/50">
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{t.name}</span>
          <span className="text-xs text-muted-foreground">{t.fields.length} field{t.fields.length === 1 ? "" : "s"}</span>
          {t.has_signature_marker && <Signature className={`h-3.5 w-3.5 ${signatory?.signature_r2_key ? "text-emerald-600" : "text-muted-foreground"}`} />}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{relative(t.created_at)}</span>
          {canDelete && (
            <div onClick={(e) => e.stopPropagation()}>
              <ConfirmDelete testid={`del-tmpl-${t.id}`} title={`Delete ${t.name}?`}
                trigger={<button className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                onConfirm={() => onDelete(t.id)} />
            </div>
          )}
        </div>
      </div>
      {open && (
        <div className="border-t border-border/60 bg-muted/20 p-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Detected Fields</p>
          <div className="space-y-2">
            {fields.map((f, i) => (
              <div key={f.key} className="grid grid-cols-12 items-center gap-2">
                <span className="col-span-3 truncate text-xs text-muted-foreground" title={f.key}>{`{{ ${f.key} }}`}</span>
                <Input data-testid={`tmpl-field-label-${t.id}-${f.key}`} className="col-span-6" value={f.label}
                  disabled={!canEdit} onChange={(e) => setField(i, { label: e.target.value })} />
                <Select value={f.type} onValueChange={(v) => setField(i, { type: v })} disabled={!canEdit}>
                  <SelectTrigger className="col-span-3" data-testid={`tmpl-field-type-${t.id}-${f.key}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{FIELD_TYPES.map((ft) => <SelectItem key={ft} value={ft} className="capitalize">{ft}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
          {canEdit && (
            <div className="mt-3 flex justify-end">
              <Button size="sm" data-testid={`tmpl-save-fields-${t.id}`} onClick={() => saveFields.mutate()} disabled={saveFields.isPending}>Save Fields</Button>
            </div>
          )}

          {t.has_signature_marker && (
            <div className="mt-4 border-t border-border/60 pt-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Signatory</p>
              <p className="mb-2 text-xs text-muted-foreground">
                The signature image stamped at the <code>{"{{ signature }}"}</code> marker is pulled from this employee's record — set it once in Settings → Signature Directory, and every template pointing at them picks it up automatically.
              </p>
              <Select value={t.signatory_employee_id || ""} onValueChange={(v) => setSignatory.mutate(v)} disabled={!canEdit || setSignatory.isPending}>
                <SelectTrigger className="max-w-xs" data-testid={`tmpl-signatory-${t.id}`}><SelectValue placeholder="No signatory selected" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.designation ? ` — ${e.designation}` : ""}</SelectItem>)}</SelectContent>
              </Select>
              {t.signatory_employee_id && !signatory?.signature_r2_key && (
                <button onClick={() => navigate("/settings/signature-directory")}
                  className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-600 hover:underline">
                  <AlertTriangle className="h-3.5 w-3.5" />{signatory?.name || "This employee"} has no signature image on file yet — add one in Signature Directory
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DocumentTemplates() {
  const qc = useQueryClient();
  const { can } = useApp();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "" });
  const [tmplFile, setTmplFile] = useState(null);
  const [tmplProgress, setTmplProgress] = useState(null);
  const [search, setSearch] = useState("");
  const { data: templates, isLoading } = useQuery({ queryKey: ["doc-templates", "hrms"], queryFn: () => get("/doc-templates?module=hrms") });
  const { data: employees = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });

  const create = useMutation({
    mutationFn: (fd) => uploadFile("/doc-templates", fd, setTmplProgress),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["doc-templates", "hrms"] }); toast.success("Template uploaded"); setAdding(false); setForm({ name: "" }); setTmplFile(null); setTmplProgress(null); },
    onError: (e) => { setTmplProgress(null); toast.error(e?.response?.data?.detail || "Could not upload template"); },
  });
  const remove = useMutation({
    mutationFn: (tid) => del(`/doc-templates/${tid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["doc-templates", "hrms"] }); toast.success("Template removed"); },
  });

  const canEdit = can("hrms", "edit");
  const canDelete = can("hrms", "delete");

  const filteredTemplates = useMemo(() => {
    return (templates || []).filter((t) => {
      if (search && !t.name?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [templates, search]);

  return (
    <div>
      <PageHeader title="Document Templates" subtitle="Upload a .docx with {{ field }} markers — fields are detected automatically and can be filled in to generate real documents.">
        {canEdit && (
          <Button data-testid="add-template-btn" onClick={() => setAdding((s) => !s)}>
            <Plus className="mr-1.5 h-4 w-4" />Add Template
          </Button>
        )}
      </PageHeader>

      {!isLoading && templates?.length > 0 && (
        <div className="mb-4">
          <SearchInput testid="template-search" placeholder="Search template name" value={search} onChange={setSearch} />
        </div>
      )}

      {isLoading ? <Skeleton className="h-64" /> : (
        <Section>
          {adding && (
            <div className="border-b border-border/60 bg-muted/30 p-4">
              <Label>File (.docx) *</Label>
              <input type="file" accept=".docx" data-testid="template-file" className="mt-1 block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground"
                onChange={(e) => setTmplFile(e.target.files?.[0] || null)} />
              <Label className="mt-3 block">Template Name (optional)</Label>
              <Input data-testid="template-name" className="mt-1" placeholder={tmplFile?.name || "e.g. Offer Letter Template.docx"}
                value={form.name} onChange={(e) => setForm({ name: e.target.value })} />
              <UploadProgress percent={tmplProgress} />
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)} disabled={create.isPending}>Cancel</Button>
                <Button size="sm" data-testid="template-submit" disabled={!tmplFile || create.isPending} onClick={() => {
                  const fd = new FormData();
                  fd.append("file", tmplFile);
                  if (form.name) fd.append("name", form.name);
                  fd.append("module", "hrms");
                  create.mutate(fd);
                }}>Upload</Button>
              </div>
            </div>
          )}
          {!templates?.length ? (
            <EmptyState icon={FileText} title="No templates yet" description="Add a .docx with {{ field }} markers to start generating real documents." />
          ) : !filteredTemplates.length ? (
            <EmptyState icon={FileText} title="No matching templates" description="Adjust your search." />
          ) : (
            <div className="divide-y divide-border">
              {filteredTemplates.map((t) => (
                <TemplateRow key={t.id} t={t} canEdit={canEdit} canDelete={canDelete} onDelete={(tid) => remove.mutate(tid)} employees={employees} />
              ))}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
